import { ICustomResponseCode } from "hooks/translations.interfaces";
import { useTranslations } from "hooks/useTranslations";
import React from "react";
import { IServerError, IShowErrors } from "./responsePopup.interfaces";
import { searchQuerySelector } from "redux/searchQuery";
import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";
import { ISearchForm, setSearchForm } from "redux/searchForm";
import { ResponseIconMap } from "./ResponseIconMap";
import { CtaDialog, deepCloneObject } from "@thekilroygroup/shared-ui";

interface IProps {
  errorResult: any;
  setShowErrorPopup: (boolean: boolean) => void;
  triggerRefetch: () => void;
}

export const ErrorHandler = (props: IProps) => {
  const {
    errorResult,
    setShowErrorPopup = () => {},
    triggerRefetch = () => {},
  } = props;
  const { translations } = useTranslations();
  const searchQuery = useSelector(searchQuerySelector);
  const dispatch = UseAppDispatch();
  const { error } = errorResult;

  const showErrors = (errors: IShowErrors) => {
    const { dates, destination, metaPartnerRedirectQuery } = errors;

    let nextQuery: ISearchForm = deepCloneObject(searchQuery);
    nextQuery.flights.forEach((flight) => {
      if (dates) {
        flight.departureDate = null;
        flight.validation.departureDate.dirty = true;
        flight.validation.departureDate.valid = false;
      }
      if (destination) {
        if (flight.arrival === null || flight.arrival.length !== 3) {
          flight.arrival = null;
          flight.validation.arrival.dirty = true;
          flight.validation.arrival.valid = false;
        }
        if (flight.departure === null || flight.departure.length !== 3) {
          flight.departure = null;
          flight.validation.departure.dirty = true;
          flight.validation.departure.valid = false;
        }
      }
    });
    if (metaPartnerRedirectQuery) {
      nextQuery.fareId = null;
    }

    dispatch(setSearchForm(nextQuery));
  };

  const getErrorType = (error: IServerError) => {
    if (
      !error.data ||
      !error.data?.customErrors ||
      error.data?.customErrors.length === 0
    ) {
      const errorTranslations =
        translations.customResponseCodes["couldNotLoad"];

      return (
        <CtaDialog
          {...errorTranslations}
          onClose={() => {
            setShowErrorPopup(false);
            showErrors({
              dates: true,
              destination: true,
              metaPartnerRedirectQuery: true,
            });
          }}
          ctaAction={() => {
            setShowErrorPopup(false);
            showErrors({
              dates: true,
              destination: true,
              metaPartnerRedirectQuery: true,
            });
          }}
          cta2Action={() => {
            setShowErrorPopup(false);
            triggerRefetch();
          }}
          icon={
            (errorTranslations.icon &&
              ResponseIconMap[errorTranslations.icon]) ||
            undefined
          }
        />
      );
    }

    const customResponseCodes: ICustomResponseCode[] =
      error.data.customErrors.map((code) => code.errorCode);

    const errorTranslations =
      translations.customResponseCodes[
        customResponseCodes[0] as keyof typeof translations.customResponseCodes
      ] || translations.customResponseCodes.somethingWentWrong;

    let errors = {
      dates: false,
      destination: false,
      metaPartnerRedirectQuery: false,
    };

    switch (customResponseCodes[0]) {
      case "4001": // INVALID_DESTINATION
        errors.destination = true;
        break;

      case "4002": // INVALID_DATE
        errors.dates = true;
        break;

      case "4003": // INVALID_DATE_ORDER
        errors.dates = true;
        break;

      case "4004": // INVALID_DOMAIN
        errors.destination = true;
        errors.dates = true;
        break;

      case "4007": // INVALID_DOMAIN
        errors.destination = false;
        errors.dates = false;
        break;

      case "4008": // INVALID_META_REDIRECT_QUERY
        errors.metaPartnerRedirectQuery = true;
        break;

      default:
        errors.destination = false;
        errors.dates = false;
        break;
    }

    return (
      <CtaDialog
        {...errorTranslations}
        onClose={() => {
          setShowErrorPopup(false);
          showErrors(errors);
        }}
        ctaAction={() => {
          setShowErrorPopup(false);
          showErrors(errors);
        }}
        cta2Action={() => {
          setShowErrorPopup(false);
          triggerRefetch();
        }}
        icon={
          (errorTranslations.icon && ResponseIconMap[errorTranslations.icon]) ||
          undefined
        }
      />
    );
  };

  return <>{getErrorType(error)}</>;
};
